/**
 * Soulo Home Gallery — lightweight storefront enhancements
 */
(function () {
  function initStickyAtc() {
    const sticky = document.querySelector('[data-soulo-sticky-atc]');
    const form = document.querySelector('product-form-component form, form[action*="/cart/add"]');
    if (!sticky || !form) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(form);
  }

  function initShare() {
    document.querySelectorAll('[data-soulo-share]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const url = btn.dataset.url || window.location.href;
        const title = btn.dataset.title || document.title;
        try {
          if (navigator.share) {
            await navigator.share({ title, url });
          } else if (navigator.clipboard) {
            await navigator.clipboard.writeText(url);
            const original = btn.textContent;
            btn.textContent = btn.dataset.copiedLabel || 'Copied';
            setTimeout(() => {
              btn.textContent = original;
            }, 1600);
          }
        } catch (e) {
          /* user cancelled */
        }
      });
    });
  }

  function initRecentlyViewed() {
    const root = document.querySelector('[data-soulo-recently-viewed]');
    const currentHandle = root?.dataset.currentHandle;
    const KEY = 'soulo_recently_viewed_v1';

    if (currentHandle) {
      try {
        const list = JSON.parse(localStorage.getItem(KEY) || '[]');
        const next = [currentHandle, ...list.filter((h) => h !== currentHandle)].slice(0, 12);
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch (e) { /* ignore */ }
    }

    if (!root) return;
    const grid = root.querySelector('[data-soulo-recent-grid]');
    if (!grid) return;

    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem(KEY) || '[]');
    } catch (e) {
      handles = [];
    }
    handles = handles.filter((h) => h && h !== currentHandle).slice(0, Number(root.dataset.limit || 4));
    if (!handles.length) {
      root.hidden = true;
      return;
    }

    // Cards are pre-rendered server-side for known products when possible;
    // otherwise hide items not in the stored list.
    const cards = [...grid.querySelectorAll('[data-recent-handle]')];
    let visible = 0;
    cards.forEach((card) => {
      const show = handles.includes(card.dataset.recentHandle);
      card.hidden = !show;
      if (show) visible += 1;
    });
    if (!visible) root.hidden = true;
  }

  /**
   * PDP/About/richtext prose can contain digit fragments mid-sentence
   * (e.g. "المقاس: 180 × 80 سم"). The DOM text is already plain ASCII, but
   * on Arabic/Hebrew pages that prose renders in a system font
   * (--soulo-v2-font-body under html[lang]) whose OpenType `locl` table
   * substitutes Arabic-Indic glyphs for those digits at paint time — same
   * root cause as the price/badge fix in soulo-v2.css, but those are
   * dedicated numeric elements a CSS selector can target directly. Prose
   * mixes digits into running Arabic text with no separate element to
   * select, so here each digit run gets wrapped in a
   * <span class="soulo-latin-num"> (CSS in soulo-v2.css pins that class to
   * a Latin-only font). Text content and surrounding Arabic prose are
   * untouched — no direction changes, no rewritten copy.
   */
  function wrapLatinNumerals() {
    const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (!lang.startsWith('ar') && !lang.startsWith('he')) return;

    const roots = document.querySelectorAll('.rte, [class*="__text_aEtTtq"]');
    const digitRun = /[0-9]+(?:[.,][0-9]+)?/g;

    roots.forEach((root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue || !/[0-9]/.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (!parent || parent.closest('script, style, .soulo-latin-num')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) textNodes.push(node);

      textNodes.forEach((textNode) => {
        const text = textNode.nodeValue;
        const frag = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        digitRun.lastIndex = 0;
        while ((match = digitRun.exec(text))) {
          if (match.index > lastIndex) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          const span = document.createElement('span');
          span.className = 'soulo-latin-num';
          span.textContent = match[0];
          frag.appendChild(span);
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        textNode.parentNode.replaceChild(frag, textNode);
      });
    });
  }

  function setDocumentDirection() {
    const html = document.documentElement;
    const lang = (html.getAttribute('lang') || '').toLowerCase();
    if (lang.startsWith('ar') || lang.startsWith('he') || lang.startsWith('fa') || lang.startsWith('ur')) {
      html.setAttribute('dir', 'rtl');
    } else if (!html.getAttribute('dir')) {
      html.setAttribute('dir', 'ltr');
    }
  }

  // Shopify's predictive search endpoint (/search/suggest) rejects Arabic and
  // Hebrew buyer locales with a 417 "Unsupported buyer locale" response on
  // this store — confirmed via direct request inspection, not a theme URL
  // bug (English, with its /en/ locale prefix, gets 200 + real results).
  // Skip the fetch entirely on those locales so typing never produces a
  // failed request / empty flash; Enter still falls through to the native
  // full search page (predictive-search.js's own keydown handler already
  // does this when there is no active predictive result).
  function initSearchFallback() {
    const locale = document.documentElement.lang;
    if (locale !== 'ar' && locale !== 'he') return;

    const component = document.querySelector('predictive-search-component');
    if (!component) return;

    component.search = () => {};
  }

  document.addEventListener('DOMContentLoaded', () => {
    setDocumentDirection();
    initStickyAtc();
    initShare();
    initRecentlyViewed();
    wrapLatinNumerals();
    initSearchFallback();
  });
})();
